import { Action } from 'corso-admin-ui';
import { CrewClaimResolutionMethodEnum } from 'corso-types';
import { useState } from 'react';
import useFulfillmentLocations from '~/features/settings/automations/hooks/useFulfillmentLocations';
import { SelectedVariant } from '~/types';
import ExternalResolutionModifier from './ExternalResolutionModifier';
import ProductReplacement from './ProductReplacement';
import { ResolutionModifierProps } from './types';

export default function ReplacementOrderModifier({
  claimLineItem,
  onCancel,
  onSave,
}: ResolutionModifierProps<
  typeof CrewClaimResolutionMethodEnum.replacementOrder
>) {
  const locations = useFulfillmentLocations();
  const suggestedItem = claimLineItem.suggestedWarrantyReplacementItem
    ? ([
        {
          ...claimLineItem.suggestedWarrantyReplacementItem,
          quantity: claimLineItem.quantity,
        },
      ] satisfies SelectedVariant[])
    : [];

  const replacementItemsFromClaim = claimLineItem.replacementItems?.map(
    (replacementItem) =>
      ({
        idFromPlatform: replacementItem.idFromPlatform,
        title: replacementItem.title ?? 'No Title',
        optionsValueString:
          replacementItem.optionsValueString ?? 'Missing Name',
        imgUrl: replacementItem.imgUrl ?? '',
        price: replacementItem.price,
        sku: replacementItem.sku ?? null,
        grams: replacementItem.grams ?? 0,
        titleWithOptions: replacementItem.titleWithOptions ?? 'Missing Name',
        quantity: replacementItem.quantity,
        options: replacementItem.options ?? [],
      }) satisfies SelectedVariant,
  );

  const [wasResolvedExternally, setWasResolvedExternally] = useState(
    !!claimLineItem.wasResolvedExternally,
  );

  const [replacementItems, setReplacementItems] = useState<SelectedVariant[]>(
    replacementItemsFromClaim ?? suggestedItem,
  );

  const { name } = claimLineItem.originalStoreOrderLineItem;

  return (
    <>
      <ExternalResolutionModifier
        checked={wasResolvedExternally}
        onChange={setWasResolvedExternally}
      />
      <ProductReplacement
        locations={locations.data ?? []}
        defaultSearchTerm={name ?? ''} // maybe omit default if replacement items are already selected
        selectedVariants={replacementItems}
        onChange={setReplacementItems}
      />
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
        <Action variant="ghost" onClick={onCancel}>
          Cancel
        </Action>
        <Action
          variant="primary"
          disabled={!replacementItems.length}
          onClick={() => {
            onSave({
              wasResolvedExternally,
              resolutionMethod: CrewClaimResolutionMethodEnum.replacementOrder,
              replacementItems: replacementItems.map((item) => ({
                idFromPlatform: item.idFromPlatform,
                quantity: item.quantity,
                name: item.titleWithOptions,
                imgUrl: item.imgUrl,
                sku: item.sku ?? undefined,
                price: item.price,
              })),
            });
          }}
        >
          Save
        </Action>
      </div>
    </>
  );
}
